// ============================================================================
// useSessionWarning — Aviso proactivo de expiración de sesión
//
// Comportamiento:
//   - Chequea cada 30s cuánto queda del access token.
//   - Si quedan ≤ WARN_SECONDS (120s):
//       · Usuario activo recientemente (≤ INACTIVITY_THRESHOLD, 60s)
//         → refresh silencioso, el usuario no nota nada.
//       · Usuario inactivo (> INACTIVITY_THRESHOLD)
//         → muestra modal con countdown.
//   - Countdown llega a 0 → logout automático.
//   - "Continuar sesión" → refreshToken() → modal desaparece.
//   - "Cerrar sesión" → logout inmediato.
//
// Actividad detectada: mousemove, mousedown, keydown,
// touchstart, scroll, wheel (mismos eventos que useInactivityLogout).
// ============================================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { tokenManager } from '../api/client';
import { refreshToken } from '../api/auth';

const WARN_SECONDS = 120;              // Actuar cuando queden ≤ 2 minutos
const CHECK_INTERVAL = 30;            // Chequear cada 30 segundos
const INACTIVITY_THRESHOLD = 60;      // Refresh silencioso si activo en los últimos 60s

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

export interface SessionWarningState {
  isVisible: boolean;
  secondsLeft: number;
  onContinue: () => Promise<void>;
  onLogout: () => void;
}

export function useSessionWarning(
  isAuthenticated: boolean,
  onExpired: () => void,
): SessionWarningState {
  const [isVisible, setIsVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARN_SECONDS);

  const checkIntervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isVisibleRef         = useRef(false);
  const lastActivityRef      = useRef<number>(Date.now());
  const onExpiredRef         = useRef(onExpired);
  onExpiredRef.current = onExpired;

  // ── Rastrear actividad del usuario ─────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((initialSeconds: number) => {
    clearCountdown();
    setSecondsLeft(initialSeconds);

    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCountdown]);

  const doSilentRefresh = useCallback(async () => {
    try {
      await refreshToken();
    } catch {
      // Si el refresh silencioso falla, el refresh reactivo del apiClient
      // lo intentará de nuevo en la próxima petición.
    }
  }, []);

  // ── Logout cuando countdown llega a 0 ──────────────────────────────────────
  useEffect(() => {
    if (secondsLeft === 0 && isVisible) {
      setIsVisible(false);
      isVisibleRef.current = false;
      onExpiredRef.current();
    }
  }, [secondsLeft, isVisible]);

  // ── Acciones del modal ──────────────────────────────────────────────────────
  const onContinue = useCallback(async () => {
    try {
      await refreshToken();
    } catch {
      setIsVisible(false);
      isVisibleRef.current = false;
      clearCountdown();
      onExpiredRef.current();
      return;
    }
    setIsVisible(false);
    isVisibleRef.current = false;
    clearCountdown();
    setSecondsLeft(WARN_SECONDS);
    lastActivityRef.current = Date.now();
  }, [clearCountdown]);

  const onLogout = useCallback(() => {
    setIsVisible(false);
    isVisibleRef.current = false;
    clearCountdown();
    onExpiredRef.current();
  }, [clearCountdown]);

  // ── Chequeo periódico ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      clearCountdown();
      setIsVisible(false);
      isVisibleRef.current = false;
      return;
    }

    const check = async () => {
      const expiry = tokenManager.getTokenExpiry();
      if (!expiry) return;

      const secondsRemaining = Math.floor((expiry * 1000 - Date.now()) / 1000);

      // Token ya expirado
      if (secondsRemaining <= 0) {
        if (isVisibleRef.current) {
          setIsVisible(false);
          isVisibleRef.current = false;
          onExpiredRef.current();
        }
        return;
      }

      // Fuera de la ventana de aviso
      if (secondsRemaining > WARN_SECONDS) return;

      // Dentro de la ventana de aviso — ¿usuario activo?
      const secondsSinceActivity = (Date.now() - lastActivityRef.current) / 1000;
      const userIsActive = secondsSinceActivity <= INACTIVITY_THRESHOLD;

      if (userIsActive) {
        // Refresh silencioso — el usuario no nota nada
        if (!isVisibleRef.current) {
          await doSilentRefresh();
        }
        return;
      }

      // Usuario inactivo — mostrar modal si no está ya visible
      if (!isVisibleRef.current) {
        isVisibleRef.current = true;
        setIsVisible(true);
        startCountdown(secondsRemaining);
      }
    };

    check();
    checkIntervalRef.current = setInterval(check, CHECK_INTERVAL * 1000);

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [isAuthenticated, doSilentRefresh, startCountdown, clearCountdown]);

  return { isVisible, secondsLeft, onContinue, onLogout };
}
